import { supabase, SupabaseNotConfiguredError } from "../supabase";
import type { Concept } from "../db";
import type { Database } from "../database.types";

type ConceptDbRow = Database["public"]["Tables"]["concepts"]["Row"];
type ConceptInsert = Database["public"]["Tables"]["concepts"]["Insert"];

function rowToConcept(row: ConceptDbRow & { sync_version?: number }): Concept {
  return {
    syncVersion: row.sync_version,
    id: row.id,
    entityType: (row.entity_type as Concept["entityType"]) ?? ("word" as Concept["entityType"]),
    frequency: row.frequency ?? undefined,
    topic: (row.topic as Concept["topic"]) ?? undefined,
    ageGroup: (row.age_group as Concept["ageGroup"]) ?? undefined,
    level: (row.level as Concept["level"]) ?? undefined,
    packId: row.pack_id ?? "user",
  };
}

function conceptToInsert(userId: string, c: Concept): ConceptInsert {
  return {
    id: c.id,
    owner_id: userId,
    entity_type: c.entityType ?? null,
    frequency: c.frequency ?? null,
    topic: c.topic ?? null,
    age_group: c.ageGroup ?? null,
    level: c.level ?? null,
    pack_id: c.packId ?? null,
  };
}

const CHUNK_SIZE = 500;

export async function pushConcepts(userId: string, concepts: Concept[]): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  for (let i = 0; i < concepts.length; i += CHUNK_SIZE) {
    const chunk = concepts.slice(i, i + CHUNK_SIZE).map((c) => conceptToInsert(userId, c));
    const { error } = await supabase.from("concepts").upsert(chunk, { onConflict: "owner_id,id" });
    if (error) throw error;
  }
}

export async function pushConcept(userId: string, concept: Concept): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase
    .from("concepts")
    .upsert(conceptToInsert(userId, concept), { onConflict: "owner_id,id" });
  if (error) throw error;
}

export async function pullConcepts(userId: string): Promise<Concept[]> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const rows: ConceptDbRow[] = [];
  let after: string | undefined;
  for (;;) {
    let query = supabase.from("concepts").select("*")
      .eq("owner_id", userId).order("id").limit(500);
    if (after !== undefined) query = query.gt("id", after);
    const { data, error } = await query;
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data ?? []).length < 500) break;
    after = data![data!.length - 1].id;
  }
  return rows.map(rowToConcept);
}

export async function deleteConceptRemote(userId: string, id: string): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase.from("concepts").delete().eq("id", id).eq("owner_id", userId);
  if (error) throw error;
}
